'use client';

import React, { useEffect, useState } from 'react';
import Cookies from 'universal-cookie';

import { getLogger } from '@lib/logging';

const logger = getLogger('CsrfSession');

class ResponseError extends Error {
  response: Response;

  constructor(message, response) {
    super(message);
    this.response = response;
    this.name = 'ResponseError';
  }
}

/**
 * Makes a query to initialize a csrf_token cookie.
 * Some unauthenticated (logged out) pages, such as Reset Password, use
 * Identity endpoints that enforce CSRF verification.
 */
async function initCsrfSession() {
  // For endpoints that handle cookies/session, the best practice is to
  // query the service directly instead of using GraphQL.
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_NERDWALLET_EXTERNAL_API_BASE_URL}/identity/api/v1/init_csrf_session`,
      {
        headers: {
          'Content-type': 'application/json',
          'X-Caller-Client-ID': process.env.CALLER_CLIENT_ID,
          'X-Client-Platform': 'web',
        },
      },
    );

    if (!response.ok) {
      throw new ResponseError('Response not OK', response);
    }
  } catch (e) {
    logger.error('Failed to init CSRF token', e.message);
  }
}

type InitCsrfSessionProps = {
  children: React.ReactNode;
};

const CsrfSession = ({ children }: InitCsrfSessionProps): React.ReactNode => {
  const [hasCsrfToken, setHasCsrfToken] = useState(false);
  useEffect(() => {
    async function initCsrf() {
      const cookies = new Cookies();
      const csrfToken = cookies.get('csrf_token');

      if (!csrfToken) {
        await initCsrfSession();
      }

      setHasCsrfToken(true);
    }

    initCsrf();
  }, []);

  if (hasCsrfToken) {
    return children;
  }

  return null;
};

export default CsrfSession;
